/**
 * History page sticky timeline nav.
 * Tracks which .history-chapter is currently in view (native IntersectionObserver,
 * no manual scroll listener) and updates the year column + title accordingly.
 *
 * The year column lists every chapter's year, stacked. On desktop
 * (component-history-timeline-nav.css shows 5 rows) the active year sits
 * centered at opacity 1, with two fading neighbors above/below; on mobile
 * only one row shows, so it just displays the current year. Either way the
 * same --active-index custom property drives the translateY that "scrolls"
 * the column, so no breakpoint branching is needed here.
 *
 * The chapter title mirrors this on mobile: setupTitleTrack() builds an
 * equivalent stack of every chapter's title, driven by the same
 * --active-index property so it slides in lockstep with the year column
 * (direction falls out of the index math for free). Desktop instead shows
 * the plain-fade .history-timeline-nav__title element — see setTitle().
 *
 * On desktop the nav docks natively: it's position: sticky inside
 * .history-timeline-nav__dock-range, a proxy element setupDockRange() sizes
 * (via ResizeObserver) to run from half a nav-height above the first
 * chapter's vertical middle to half a nav-height below the last chapter's.
 * Sticky's own behavior gives us, for free: the nav resting at the first
 * chapter's middle by default on load, sticking centered in the viewport
 * once scrolled that far, and releasing — scrolling away with the page —
 * once scrolled past the last chapter's middle; the half-nav-height padding
 * on each end just gives it room to fully dock/undock instead of clipping in
 * right at that edge. See the comment at the top of
 * sections/history-timeline-nav.liquid for why dock-range can't just be a
 * real wrapper around the chapters.
 */
// Opacity per distance from the active year is CSS's job (component-history-
// timeline-nav.css keys off [data-distance], with a wider/brighter set of
// rules on :hover) — this just reports how far each year is from the active
// one so CSS can decide what that means.

class HistoryTimelineNav extends HTMLElement {
  connectedCallback() {
    this.titleEl = this.querySelector('[data-history-nav-title]');
    this.yearList = this.querySelector('[data-history-nav-year-list]');

    this.chapters = Array.from(document.querySelectorAll('[data-history-chapter]'));
    if (!this.chapters.length || !this.titleEl || !this.yearList) return;

    this.yearItems = this.chapters.map((chapter) => {
      const item = document.createElement('span');
      item.className = 'history-timeline-nav__year-item';
      item.textContent = chapter.dataset.year || '';
      this.yearList.append(item);
      return item;
    });

    this.setupTitleTrack();

    this.setActiveIndex(0);

    // Treat the middle 10% of the viewport as the "active" band — whichever
    // chapter crosses it becomes the current one.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.chapters.indexOf(entry.target);
          if (index !== -1) this.setActiveIndex(index);
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );

    this.chapters.forEach((chapter) => this.observer.observe(chapter));

    this.setupDockRange();
    this.setupMobileTapToOpen();
    this.setupChapterVisibility();
  }

  // Mobile-only equivalent of the year column: a full stack of every
  // chapter's title, inserted right after the desktop title element (never
  // before it — component-history-timeline-nav.css's hover rule for desktop
  // depends on .history-timeline-nav__title staying the immediate sibling
  // after .history-timeline-nav__year-wrapper). CSS hides whichever one the
  // current breakpoint doesn't need. setActiveIndex drives this list's
  // translateY with the same --active-index property and transition token
  // as the year list, so both slide in lockstep and direction (up vs down)
  // falls out of the index math for free, instead of needing to be tracked.
  setupTitleTrack() {
    this.titleTrack = document.createElement('div');
    this.titleTrack.className = 'history-timeline-nav__title-track';

    this.titleList = document.createElement('div');
    this.titleList.className = 'history-timeline-nav__title-list body-m-inter';
    this.titleTrack.append(this.titleList);

    this.titleEl.after(this.titleTrack);

    this.titleItems = this.chapters.map((chapter) => {
      const item = document.createElement('div');
      item.className = 'history-timeline-nav__title-item';
      item.textContent = chapter.dataset.title || '';
      this.titleList.append(item);
      return item;
    });
  }

  // The nav has nothing to show once no chapter is on screen at all — before
  // the first one (still over history-intro) or after the last one (scrolled
  // into the footer). Unlike this.observer below (a narrow -45%/-45% band
  // just for picking the ACTIVE chapter), this uses the default full-viewport
  // bounds so it only reports "gone" once every chapter has fully left.
  // Mobile only in effect (component-history-timeline-nav.css cancels the
  // opacity mask on desktop) — desktop's dock-range (setupDockRange() below)
  // already keeps the sticky nav off both edges without needing this at all.
  setupChapterVisibility() {
    const visibleChapters = new Set();

    this.chapterVisibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => visibleChapters[entry.isIntersecting ? 'add' : 'delete'](entry.target));
      this.classList.toggle('is-hidden', visibleChapters.size === 0);
    });

    this.chapters.forEach((chapter) => this.chapterVisibilityObserver.observe(chapter));
  }

  // Desktop only (component-history-timeline-nav.css only gives dock-range an
  // effect inside its @media (min-width: 1024px) block, where the nav is
  // position: sticky) — sizes .history-timeline-nav__dock-range to span from
  // half a nav-height above the first chapter's vertical middle to half a
  // nav-height below the last chapter's, in pixels relative to
  // .section-history-timeline-nav (dock-range's own positioned ancestor).
  // That range is what makes the nav stick centered in the viewport while
  // scrolling through it, and release — scrolling away with the page — once
  // past either end; see the comment at the top of
  // sections/history-timeline-nav.liquid. The extra half-nav-height padding
  // on each side gives the nav room to fully dock/undock at the first/last
  // chapter's middle instead of clipping in right at that edge. A
  // ResizeObserver (not a one-time measurement) because chapter heights (and
  // the nav's own height) can change after layout — images loading in, fonts
  // swapping, the viewport resizing.
  setupDockRange() {
    const dockRange = this.parentElement;
    const section = this.closest('.section-history-timeline-nav');
    if (!section) return;

    const firstChapter = this.chapters[0];
    const lastChapter = this.chapters[this.chapters.length - 1];

    const measure = () => {
      const sectionTop = section.getBoundingClientRect().top;
      const firstRect = firstChapter.getBoundingClientRect();
      const lastRect = lastChapter.getBoundingClientRect();
      const navHalfHeight = this.getBoundingClientRect().height / 2;

      const firstMiddle = firstRect.top - sectionTop + firstRect.height / 2;
      const lastMiddle = lastRect.top - sectionTop + lastRect.height / 2;

      dockRange.style.top = `${firstMiddle}px`;
      dockRange.style.height = `${lastMiddle - firstMiddle + navHalfHeight * 2}px`;
    };

    measure();

    this.dockRangeObserver = new ResizeObserver(measure);
    this.dockRangeObserver.observe(firstChapter);
    this.dockRangeObserver.observe(lastChapter);
    this.dockRangeObserver.observe(this);
  }

  // Desktop shows the "show all chapters" link (history-overlay's <summary>)
  // directly; component-history-timeline-nav.css hides it below 1024px, so on
  // mobile tapping anywhere in this block should open the same overlay instead.
  setupMobileTapToOpen() {
    const summary = this.querySelector('history-overlay summary');
    if (!summary) return;

    this.addEventListener('click', (event) => {
      if (window.matchMedia('(min-width: 1024px)').matches) return;

      // Without this, the real click keeps bubbling past this element after
      // summary.click() (below) synchronously opens the overlay and attaches
      // history-overlay.js's "click outside to close" body listener — that
      // listener would then catch this same event on its way up, see its
      // target isn't inside <history-overlay>, and immediately close what
      // just opened, all before the next paint.
      event.stopPropagation();
      summary.click();
    });
  }

  setActiveIndex(activeIndex) {
    const chapter = this.chapters[activeIndex];

    // Read by history-overlay.js so it can open already scrolled to the
    // chapter you were viewing, instead of always starting at the top.
    this.dataset.activeChapterId = chapter?.id || '';

    this.setTitle(chapter?.dataset.title || '');

    this.titleList.style.setProperty('--active-index', activeIndex);
    this.titleItems.forEach((item, index) => item.classList.toggle('is-active', index === activeIndex));

    this.yearList.style.setProperty('--active-index', activeIndex);
    this.yearItems.forEach((item, index) => {
      const distance = Math.abs(index - activeIndex);
      item.dataset.distance = distance;
      item.classList.toggle('is-active', distance === 0);
    });
  }

  // Desktop-only title (component-history-timeline-nav.css hides this element
  // on mobile in favor of the title track above): fades the title out, swaps
  // the text once it's invisible, then fades it back in.
  setTitle(title) {
    if (title === this.titleEl.textContent) return;

    clearTimeout(this.titleFadeTimeout);
    this.titleEl.removeEventListener('transitionend', this.titleFadeHandler);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.titleEl.textContent = title;
      return;
    }

    this.titleFadeHandler = (event) => {
      if (event.target === this.titleEl && event.propertyName === 'opacity') this.swapTitle(title);
    };
    this.titleEl.addEventListener('transitionend', this.titleFadeHandler, { once: true });
    this.titleFadeTimeout = setTimeout(() => this.swapTitle(title), 400);

    this.titleEl.classList.add('is-fading');
  }

  swapTitle(title) {
    clearTimeout(this.titleFadeTimeout);
    this.titleEl.textContent = title;
    this.titleEl.classList.remove('is-fading');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.dockRangeObserver?.disconnect();
    this.chapterVisibilityObserver?.disconnect();
  }
}

customElements.define('history-timeline-nav', HistoryTimelineNav);
